/**
 * 3D Carousel Video Gallery
 * Coverflow-style carousel with perspective and depth
 */

class VideoGallery3D {
  constructor(element) {
    this.gallery = element;
    this.container = element.querySelector('.video-gallery__container');
    this.videos = Array.from(element.querySelectorAll('.video-gallery__video'));
    this.prevBtn = element.querySelector('[data-direction="prev"]');
    this.nextBtn = element.querySelector('[data-direction="next"]');
    this.dots = Array.from(element.querySelectorAll('.video-gallery__dot'));

    if (!this.container || !this.videos.length) return;

    // Start with 3rd video (index 2) as center, or middle video if less than 3
    this.currentIndex = Math.min(2, Math.floor(this.videos.length / 2));
    this.isAnimating = false;

    this.init();
  }

  init() {
    this.setupPositions();
    this.setupControls();
    this.setupVideoControls();
    this.setupKeyboardNavigation();
    this.setupTouchSwipe();
    this.setupIntersectionObserver();
    
    // Auto-play the initial center video after video controls are setup (if enabled)
    const enableAutoplay = this.gallery.getAttribute('data-enable-autoplay') === 'true';
    if (enableAutoplay) {
      this.playVideo(this.currentIndex);
    }
  }

  /**
   * Set initial positions for all videos
   */
  setupPositions() {
    this.videos.forEach((video, index) => {
      const position = index - this.currentIndex;
      video.setAttribute('data-position', position);
    });
    this.updateDots();
  }

  /**
   * Navigate to specific index
   */
  goToSlide(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    if (index < 0 || index >= this.videos.length) return;

    this.isAnimating = true;
    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    // Pause previous video
    this.pauseVideo(previousIndex);

    // Update positions with infinite rotation
    this.videos.forEach((videoEl, i) => {
      const position = this.getCircularPosition(i, this.currentIndex);
      videoEl.setAttribute('data-position', position);
    });

    this.updateDots();
    this.updateButtonStates();

    // Auto-play the new center video after animation completes (if enabled)
    const enableAutoplay = this.gallery.getAttribute('data-enable-autoplay') === 'true';
    setTimeout(() => {
      if (enableAutoplay) {
        this.playVideo(index);
      }
      this.isAnimating = false;
    }, 600);
  }

  /**
   * Calculate circular position for infinite carousel
   */
  getCircularPosition(videoIndex, centerIndex) {
    const totalVideos = this.videos.length;
    let position = videoIndex - centerIndex;

    // Wrap around for infinite rotation
    if (position > totalVideos / 2) {
      position -= totalVideos;
    } else if (position < -totalVideos / 2) {
      position += totalVideos;
    }

    return position;
  }

  /**
   * Pause video at specific index
   */
  pauseVideo(index) {
    if (index < 0 || index >= this.videos.length) return;

    const videoWrapper = this.videos[index];
    const video = videoWrapper.querySelector('video');
    const iframe = videoWrapper.querySelector('iframe');

    if (video && !video.paused) {
      video.pause();
    }

    // Pause YouTube/Vimeo iframes
    if (iframe) {
      const src = iframe.src || '';
      
      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        // YouTube - use postMessage to pause
        try {
          iframe.contentWindow.postMessage(
            { event: 'command', func: 'pauseVideo' },
            '*'
          );
        } catch (err) {
          console.error('YouTube pause failed:', err);
        }
      } else if (src.includes('vimeo.com')) {
        // Vimeo - use postMessage to pause
        try {
          iframe.contentWindow.postMessage(
            { method: 'pause' },
            '*'
          );
        } catch (err) {
          console.error('Vimeo pause failed:', err);
        }
      }
    }
  }

  /**
   * Play video at specific index (auto-play for center video)
   */
  playVideo(index) {
    if (index < 0 || index >= this.videos.length) return;

    const videoWrapper = this.videos[index];
    const video = videoWrapper.querySelector('video');
    const iframe = videoWrapper.querySelector('iframe');

    // Get autoplay mute setting
    const autoplayMuted = this.gallery.getAttribute('data-autoplay-muted') === 'true';

    if (video) {
      // HTML5 video element - always mute for autoplay to work
      // Browser autoplay policy requires muted=true for autoplay without user interaction
      video.muted = true;
      
      // Use a small delay to ensure video is ready
      const attemptPlay = () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Auto-playing HTML5 video at index:', index, 'muted: true (required by browser)');
            })
            .catch((err) => {
              console.warn('Autoplay attempt failed (this is normal):', err.message);
              // Silently fail - don't auto-play, user will interact
            });
        }
      };

      // Try to play based on readyState
      if (video.readyState >= 2) {
        // Video data is available
        attemptPlay();
      } else {
        // Wait for video to be ready
        const onCanPlay = () => {
          attemptPlay();
          video.removeEventListener('canplay', onCanPlay);
        };
        video.addEventListener('canplay', onCanPlay);
      }
    } else if (iframe) {
      // YouTube/Vimeo iframe - use postMessage API
      this.playIframe(iframe, index);
    } else {
      console.log('No video element found at index:', index);
    }
  }

  /**
   * Play YouTube/Vimeo iframe using postMessage API
   */
  playIframe(iframe, index) {
    const src = iframe.src || '';
    const autoplayMuted = this.gallery.getAttribute('data-autoplay-muted') === 'true';

    if (src.includes('youtube.com') || src.includes('youtu.be')) {
      // YouTube - use postMessage API to play and control volume
      try {
        iframe.contentWindow.postMessage(
          { event: 'command', func: 'playVideo' },
          '*'
        );
        
        // Control mute state via postMessage
        if (autoplayMuted) {
          iframe.contentWindow.postMessage(
            { event: 'command', func: 'mute' },
            '*'
          );
        } else {
          iframe.contentWindow.postMessage(
            { event: 'command', func: 'unMute' },
            '*'
          );
        }
        
        console.log('Auto-playing YouTube video at index:', index, 'muted:', autoplayMuted);
      } catch (err) {
        console.error('YouTube autoplay failed:', err);
      }
    } else if (src.includes('vimeo.com')) {
      // Vimeo - use postMessage with different format
      try {
        iframe.contentWindow.postMessage(
          { method: 'play' },
          '*'
        );
        
        // Control mute state for Vimeo
        if (autoplayMuted) {
          iframe.contentWindow.postMessage(
            { method: 'setMuted', value: true },
            '*'
          );
        } else {
          iframe.contentWindow.postMessage(
            { method: 'setMuted', value: false },
            '*'
          );
        }
        
        console.log('Auto-playing Vimeo video at index:', index, 'muted:', autoplayMuted);
      } catch (err) {
        console.error('Vimeo autoplay failed:', err);
      }
    }
  }

  /**
   * Navigate to next/previous slide with infinite rotation
   */
  navigate(direction) {
    const newIndex = direction === 'next'
      ? (this.currentIndex + 1) % this.videos.length
      : (this.currentIndex - 1 + this.videos.length) % this.videos.length;

    this.goToSlide(newIndex);
  }

  /**
   * Setup arrow button controls
   */
  setupControls() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate('prev'));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate('next'));
    }

    // Dot navigation
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index);
        this.goToSlide(index);
      });
    });

    this.updateButtonStates();
  }

  /**
   * Update button states
   */
  updateButtonStates() {
    if (!this.prevBtn || !this.nextBtn) return;

    // Infinite carousel - arrows are always enabled
    this.prevBtn.disabled = false;
    this.nextBtn.disabled = false;
  }

  /**
   * Update navigation dots
   */
  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('video-gallery__dot--active');
      } else {
        dot.classList.remove('video-gallery__dot--active');
      }
    });
  }

  /**
   * Setup individual video controls
   */
  setupVideoControls() {
    this.videos.forEach((videoWrapper, index) => {
      const video = videoWrapper.querySelector('video');

      if (!video) {
        console.log('No video element found in wrapper', index);
        return;
      }

      // Check if video has a source
      const source = video.querySelector('source');
      if (!source || !source.src) {
        console.log('Video has no source:', index);
        return;
      }

      // Video wrapper click to toggle play/pause and unmute
      videoWrapper.addEventListener('click', (e) => {
        // Don't toggle if clicking on links
        if (e.target.closest('a')) return;

        if (index !== this.currentIndex) {
          this.goToSlide(index);
          return;
        }

        // For center video, toggle play/pause and unmute on interaction
        if (video.paused) {
          video.muted = false; // Unmute on user interaction
          video.play().catch((err) => {
            console.error('Play error:', err);
          });
        } else {
          video.pause();
        }
      });

      // Pause videos that are not in center when they try to play
      video.addEventListener('play', () => {
        if (index !== this.currentIndex) {
          video.pause();
        }
      });

      // Allow unmuting only for center video
      video.addEventListener('click', (e) => {
        e.stopPropagation();
        // Let the wrapper handle it
      });

      // Handle errors
      video.addEventListener('error', (e) => {
        console.error('Video error:', e);
      });
    });
  }

  /**
   * Keyboard navigation
   */
  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (!this.isInViewport()) return;

      const isRTL = document.documentElement.dir === 'rtl';

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.navigate(isRTL ? 'next' : 'prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.navigate(isRTL ? 'prev' : 'next');
          break;
      }
    });
  }

  /**
   * Touch swipe support
   */
  setupTouchSwipe() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.container.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const diff = startX - endX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left
        this.navigate('next');
      } else {
        // Swipe right
        this.navigate('prev');
      }
    }
  }

  /**
   * Lazy load and autoplay videos when in viewport
   */
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.3
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.querySelector('video');
        if (!video) return;

        if (entry.isIntersecting) {
          // Load video if not loaded
          if (video.dataset.src && !video.src) {
            video.src = video.dataset.src;
            video.load();
          }
        } else {
          // Pause when out of view
          video.pause();
        }
      });
    }, options);

    this.videos.forEach(videoWrapper => {
      observer.observe(videoWrapper);
    });
  }

  /**
   * Check if gallery is in viewport
   */
  isInViewport() {
    const rect = this.gallery.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Auto-rotate carousel (optional)
   */
  startAutoRotate(interval = 5000) {
    this.autoRotateInterval = setInterval(() => {
      if (this.currentIndex < this.videos.length - 1) {
        this.navigate('next');
      } else {
        this.goToSlide(0);
      }
    }, interval);
  }

  stopAutoRotate() {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
    }
  }
}

// Initialize all video galleries
function initVideoGalleries() {
  const galleries = document.querySelectorAll('.video-gallery');
  galleries.forEach(gallery => {
    new VideoGallery3D(gallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoGalleries);
} else {
  initVideoGalleries();
}

// Re-initialize on section updates (Shopify theme editor)
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initVideoGalleries);
}
